import { LockOutlined } from '@mui/icons-material';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { ToggleOptionCard } from './toggleOptionCard';
import Loader from 'components/v2/common/loader';
import Result from 'components/v2/common/Result';

import api from 'api';
import type { AdminUsers } from 'api/models/response/AdminUsers';
import type { Permission } from 'api/models/response/permissions';
import { formatPermissionName } from 'helpers/permissions';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import { getErrorMessage } from 'utils/v2/errorHelper';

interface PermissionsDialogProps {
    user: AdminUsers;
    onClose: () => void;
}

export function PermissionsDialog({ user, onClose }: PermissionsDialogProps) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const dispatch = useDispatch();
    const { translate } = useTranslate();

    const [availablePermissions, setAvailablePermissions] = useState<Permission[]>([]);
    const [assignedPermissions, setAssignedPermissions] = useState<Permission[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [updatingPermissionName, setUpdatingPermissionName] = useState<string | null>(null);

    const userFullName = useMemo(
        () =>
            `${user.firstname || ''} ${user.lastname || ''}`.trim() ||
            user.email ||
            `User #${user.id}`,
        [user]
    );

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setIsLoading(true);
            try {
                const [availableResponse, assignedResponse] = await Promise.all([
                    api.permissions.getAvailablePermissions(user.id),
                    api.permissions.getPermissions(user.id),
                ]);
                if (cancelled) return;
                setAvailablePermissions(availableResponse?.data || []);
                setAssignedPermissions(assignedResponse?.data || []);
            } catch (error) {
                if (cancelled) return;
                dispatch(
                    setAlert({
                        message: getErrorMessage(error, translate('failedToLoadPermissions')),
                        type: 'error',
                    })
                );
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        void load();
        return () => {
            cancelled = true;
        };
    }, [dispatch, translate, user.id]);

    const isAssigned = (name: string) =>
        assignedPermissions.some((permission) => permission?.name === name);

    const handleToggle = async (permission: Permission) => {
        const assigned = isAssigned(permission.name);
        setUpdatingPermissionName(permission.name);
        try {
            if (assigned) {
                await api.permissions.deletePermissions(user.id, permission.name);
                setAssignedPermissions((prev) =>
                    prev.filter((item) => item.name !== permission.name)
                );
            } else {
                await api.permissions.addUserPermission(user.id, permission.name);
                setAssignedPermissions((prev) =>
                    prev.some((item) => item.name === permission.name)
                        ? prev
                        : [...prev, permission]
                );
            }
        } catch (error) {
            dispatch(
                setAlert({
                    message: getErrorMessage(error, translate('failedToUpdatePermission')),
                    type: 'error',
                })
            );
        } finally {
            setUpdatingPermissionName(null);
        }
    };

    return (
        <Dialog
            open
            fullWidth
            maxWidth="sm"
            fullScreen={fullScreen}
            scroll="paper"
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: fullScreen ? 0 : 2,
                        maxHeight: fullScreen ? '100%' : '90vh',
                    },
                },
            }}
        >
            <DialogTitle sx={{ py: 2 }}>
                <Typography
                    variant="h6"
                    component="span"
                    sx={{ fontWeight: 700, display: 'block' }}
                >
                    {translate('permissions')}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap sx={{ mt: 0.5 }}>
                    {userFullName}
                </Typography>
            </DialogTitle>

            <DialogContent dividers sx={{ minHeight: 200 }}>
                {isLoading ? (
                    <Loader isCircular />
                ) : availablePermissions.length === 0 ? (
                    <Result
                        icon={<LockOutlined sx={{ fontSize: 32 }} />}
                        title={translate('noPermissionsAvailable')}
                        description={translate('noPermissionsAvailableDescription')}
                    />
                ) : (
                    <Stack spacing={1}>
                        {availablePermissions.map((permission) => (
                            <ToggleOptionCard
                                key={permission.name}
                                title={formatPermissionName(permission.name)}
                                description={permission.description?.trim() || undefined}
                                selected={isAssigned(permission.name)}
                                busy={updatingPermissionName === permission.name}
                                disabled={Boolean(updatingPermissionName)}
                                onToggle={() => void handleToggle(permission)}
                            />
                        ))}
                    </Stack>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 1.5 }}>
                <Button onClick={onClose} variant="outlined" sx={{ textTransform: 'none' }}>
                    {translate('close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default PermissionsDialog;
